import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Check, Crown, Heart, Waves, Lock, Gift, Loader2, Plus, Megaphone, Search, X, Plane, Sparkles } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { CATEGORIES_ABONNEMENT as CATEGORIES } from "@/lib/categories";
import { getUser, formatFCFA, formatDate } from "@/lib/user";

const LIBELLE_PERIODE = { decouverte: "24 heures", week: "semaine", month: "mois" };
import { ChoixOperateurModal } from "@/components/ChoixOperateurModal";
import { PaiementTransfert } from "@/components/PaiementTransfert";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const catIcons = { lago: Waves, love: Heart, vip: Crown, air: Plane };
// Pack Tout-Accès : prix cumulé rubrique par rubrique, pour afficher l'économie réelle.
const PRIX_SEPARES = { decouverte: 4000, week: 12500, month: 38900 };
const nomOffre = (sub) =>
  sub?.kind === "pack" ? "Pack Tout-Accès" : CATEGORIES[sub?.category]?.name || sub?.category;

export default function Subscription() {
  const user = getUser();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // Lien de renouvellement en 1 clic (SMS de rappel) : ?periode=week&renouveler=lago
  const [period, setPeriod] = useState(
    ["decouverte", "week", "month"].includes(params.get("periode")) ? params.get("periode") : "month",
  );
  const [pricing, setPricing] = useState(null);
  const [access, setAccess] = useState(null);
  const [buying, setBuying] = useState(null);
  const [guideAnnonce, setGuideAnnonce] = useState(null);
  const [paiement, setPaiement] = useState({ live: true, operateurs: [], paiement_direct: false });
  const [offre, setOffre] = useState(null);        // offre en cours de paiement
  const [operateurEnCours, setOperateurEnCours] = useState(null);
  const [transfert, setTransfert] = useState(null);   // repli Mobile Money si panne passerelle
  const [lienProche, setLienProche] = useState(null); // lien de paiement envoyé à un tiers

  const refresh = useCallback(() => {
    if (user) axios.get(`${API}/access/${user.id}`).then((r) => setAccess(r.data));
  }, [user?.id]);

  useEffect(() => {
    axios.get(`${API}/pricing`).then((r) => setPricing(r.data));
    axios.get(`${API}/paiements/mode`).then((r) => setPaiement(r.data)).catch(() => {});
    refresh();
  }, [refresh]);

  useEffect(() => {
    const cat = params.get("cat");
    if (cat) window.scrollTo(0, 0);
  }, [params]);

  // Étape 1 : le membre choisit son opérateur (Orange, MTN, Moov, Wave)
  const buy = (kind, category, p) => {
    if (!user) return;
    const montant = pricing?.[category]?.[kind]?.[p];
    setOffre({ kind, category, period: p, montant });
  };

  // Étape 2 : initialisation du paiement puis redirection vers la page CinetPay
  const payer = async (operateur, numero, options = {}) => {
    const { kind, category, period } = offre;
    setOperateurEnCours(operateur);
    setBuying(`${kind}-${category}`);
    try {
      const res = await axios.post(`${API}/subscriptions/checkout`, {
        profile_id: user.id, kind, category, period, operateur, numero_paiement: numero,
        payeur_tiers: !!options.payeur_tiers, payeur_nom: options.payeur_nom || null,
      });
      if (res.data.payeur_tiers) {
        // Le proche paie depuis son propre téléphone : on ne redirige pas le bénéficiaire.
        localStorage.setItem("fodindin_transaction", res.data.transaction_id);
        setOffre(null);
        setLienProche({
          lien: res.data.lien_paiement, numero: res.data.payeur_numero,
          sms: res.data.sms_envoye, montant: offre.montant,
          nom: options.payeur_nom, categorie: category,
        });
        toast.success("Demande envoyée à votre proche", {
          description: "Dès qu'il paie, votre abonnement s'active sur VOTRE compte.",
        });
        return;
      }
      if (res.data.mode === "live" && res.data.payment_url) {
        // Filet de sécurité : si CinetPay revient sans référence, on la retrouve ici.
        localStorage.setItem("fodindin_transaction", res.data.transaction_id);
        toast.info(`Redirection vers ${operateur}…`);
        window.location.assign(res.data.payment_url);
        return;
      }
      // Offre gratuite ou passerelle non activée : accès ouvert immédiatement
      const sub = res.data.subscription;
      toast.success(
        kind === "addon"
          ? `Accès ${CATEGORIES[category].name} ajouté !`
          : `Abonnement ${nomOffre({ kind, category })} activé !`,
        { description: `Valable jusqu'au ${formatDate(sub.expires_at)}` }
      );
      setOffre(null);
      refresh();
      if (kind !== "addon") {
        const cible = kind === "pack" ? "lago" : category;
        setGuideAnnonce(cible);
        setTimeout(() => navigate(`/categorie/${cible}/annonces`), 2600);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail?.code === "NUMERO_INVALIDE") {
        toast.error("Numéro Mobile Money invalide", {
          description: detail.aide || "Vérifiez votre numéro à 10 chiffres et réessayez.",
        });
      } else if (detail?.code === "PAIEMENT_INDISPONIBLE") {
        // La passerelle est en panne : on propose le transfert Mobile Money plutôt
        // que de renvoyer le client sans solution.
        if (detail.repli_disponible) {
          toast.error("Opérateur momentanément injoignable", {
            description: "Payez par transfert Mobile Money : validation en moins de 30 minutes.",
          });
          // Fermer la modale de choix d'opérateur : deux fenêtres plein écran ne
          // doivent jamais coexister, sinon le repli reste inaccessible.
          setOffre(null);
          setTransfert({ kind, category, period, montant: offre.montant });
        } else {
          toast.error("Opérateur momentanément injoignable", { description: detail.aide });
        }
      } else {
        toast.error(typeof detail === "string" ? detail : detail?.message || "Erreur lors du paiement");
      }
    } finally {
      setOperateurEnCours(null);
      setBuying(null);
    }
  };

  if (!user) {
    return (
      <div className="bg-[#FAF8F5] min-h-screen">
        <Navbar />
        <main className="max-w-xl mx-auto px-4 pt-40 pb-24 text-center">
          <Lock size={44} className="mx-auto text-[#E05638] mb-6" />
          <h1 className="font-heading text-3xl font-extrabold text-stone-900">Créez d'abord votre profil</h1>
          <p className="text-stone-600 mt-4">
            {period === "decouverte"
              ? "Deux informations suffisent (nom + téléphone), puis votre accès découverte 24 h est disponible dès 500 FCFA."
              : "Les abonnements sont liés à votre profil FodindinCa."}
          </p>
          <Link
            to={`/inscription${params.get("cat") ? `?cat=${params.get("cat")}` : ""}`}
            data-testid="sub-register-link" className="mt-8 inline-block bg-[#E05638] hover:bg-[#C84428] text-white font-semibold px-8 py-4 rounded-full transition-colors">
            Créer mon profil gratuit
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const base = access?.base;
  const highlight = params.get("cat");

  return (
    <div className="bg-[#FAF8F5] min-h-screen">
      <Navbar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-24">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <span className="text-[#E05638] text-sm font-semibold tracking-[0.25em] uppercase">Abonnements</span>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-stone-900 mt-3">
            Choisissez votre accès, {user.firstname}
          </h1>
          <p className="text-stone-600 mt-4 max-w-2xl leading-relaxed">
            Chaque catégorie est exclusive : votre abonnement de base ouvre une seule porte. Ajoutez des accès croisés quand vous voulez.
          </p>
          {paiement.live ? (
            <div className="mt-5 flex flex-wrap items-center gap-2" data-testid="paiement-operateurs">
              <span className="text-xs font-semibold text-stone-500 uppercase tracking-wider">Paiement sécurisé par</span>
              {paiement.operateurs.map((op) => (
                <span key={op} className="text-xs font-semibold bg-white border border-[#E5DFD5] text-stone-700 rounded-full px-3 py-1.5">
                  {op}
                </span>
              ))}
            </div>
          ) : (
            <p className="text-xs text-stone-500 mt-2 italic" data-testid="paiement-mode-demo">
              Mode démonstration : activation immédiate, aucun débit réel.
            </p>
          )}
        </motion.div>

        {/* statut actuel */}
        {access && (
          <div className="mt-8 bg-white border border-[#E5DFD5] rounded-2xl p-6" data-testid="access-status">
            <h3 className="font-heading font-bold text-stone-900 mb-3">Vos accès actifs</h3>
            <div className="flex flex-wrap gap-3">
              {Object.values(CATEGORIES).map((cat) => {
                const has = access.access[cat.id];
                const isLoyalty = cat.id === "love" && has && access.vip_monthly_loyalty && base?.category !== "love" && !access.addons.some((a) => a.category === "love");
                return (
                  <span
                    key={cat.id}
                    data-testid={`access-badge-${cat.id}`}
                    className={`inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-full border ${has ? "text-white border-transparent" : "text-stone-400 bg-stone-100 border-stone-200"}`}
                    style={has ? { backgroundColor: cat.color } : {}}
                  >
                    {has ? <Check size={14} /> : <Lock size={14} />} {cat.name}
                    {isLoyalty && <span className="text-[10px] bg-white/25 px-2 py-0.5 rounded-full">Offert VIP</span>}
                  </span>
                );
              })}
            </div>
            {base && (
              <p className="text-xs text-stone-500 mt-3">
                Abonnement de base : <strong>{nomOffre(base)}</strong> ({LIBELLE_PERIODE[base.period] || base.period}) — expire le {formatDate(base.expires_at)}
              </p>
            )}
          </div>
        )}

        {/* toggle période */}
        <div className="mt-12 flex items-center gap-3">
          {[["decouverte", "Découverte 24 h"], ["week", "Semaine"], ["month", "Mois"]].map(([p, label]) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              data-testid={`period-toggle-${p}`}
              className={`px-6 py-2.5 rounded-full text-sm font-semibold border transition-all ${period === p ? "bg-[#12100E] text-white border-transparent" : "bg-white text-stone-600 border-[#E5DFD5]"}`}
            >
              {label}
            </button>
          ))}
        </div>

        {period === "decouverte" && (
          <div
            className="mt-6 rounded-2xl border border-[#D4AF37] bg-[#FFFBEF] px-5 py-4"
            data-testid="bandeau-decouverte"
          >
            <p className="text-sm text-stone-700">
              <strong>Offre découverte — 500 FCFA (Lago et Love), 1 500 FCFA (Club VIP et Air-Bengue).</strong> Accès
              complet à la zone de votre choix pendant <strong>24 heures</strong>, payable par Orange
              Money, MTN, Moov ou Wave. Idéal pour essayer avant de prendre la semaine ou le mois.
            </p>
          </div>
        )}

        {/* offres de base */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
          {pricing && Object.values(CATEGORIES).map((cat) => {
            const Icon = catIcons[cat.id];
            const price = pricing[cat.id].base[period];
            const isCurrent = base?.category === cat.id && base?.period === period;
            const isBaseCat = base?.category === cat.id;
            return (
              <div
                key={cat.id}
                className={`relative bg-white rounded-3xl border-2 p-7 flex flex-col gap-4 transition-all ${highlight === cat.id ? "shadow-xl" : "shadow-sm"}`}
                style={{ borderColor: highlight === cat.id || isBaseCat ? cat.color : "#E5DFD5" }}
                data-testid={`plan-card-${cat.id}`}
              >
                {cat.id === "vip" && period === "month" && (
                  <span className="absolute -top-3 left-6 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider bg-[#D4AF37] text-[#12100E] px-3 py-1 rounded-full">
                    <Gift size={12} /> Love offert
                  </span>
                )}
                <Icon size={30} strokeWidth={1.5} style={{ color: cat.color }} />
                <div>
                  <h3 className="font-heading text-xl font-extrabold text-stone-900">{cat.name}</h3>
                  <p className="text-xs text-stone-500 mt-1">{cat.subtitle}</p>
                </div>
                <div className="font-heading text-3xl font-extrabold" style={{ color: cat.color }}>
                  {formatFCFA(price)}
                  <span className="text-sm font-medium text-stone-500"> / {LIBELLE_PERIODE[period]}</span>
                </div>
                {cat.id === "vip" && period === "month" && (
                  <p className="text-xs text-[#0A5C36] font-semibold">🎁 Fidélité : accès FodindinLove inclus gratuitement</p>
                )}
                <button
                  onClick={() => buy("base", cat.id, period)}
                  disabled={buying !== null || isCurrent}
                  data-testid={`buy-base-${cat.id}`}
                  className="mt-auto w-full inline-flex items-center justify-center gap-2 text-white font-semibold py-3 rounded-full transition-opacity hover:opacity-90 disabled:opacity-50"
                  style={{ backgroundColor: cat.color }}
                >
                  {buying === `base-${cat.id}` ? <Loader2 size={16} className="animate-spin" /> : null}
                  {isCurrent ? "Abonnement actif" : isBaseCat ? "Changer de période" : base ? "Changer de base" : paiement.live ? "Payer par Mobile Money" : "S'abonner"}
                </button>
              </div>
            );
          })}
        </div>

        {/* Pack Tout-Accès : les 4 rubriques d'un coup, à prix doux */}
        {pricing?.tout && (
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            data-testid="plan-card-pack"
            className="mt-6 overflow-hidden rounded-3xl bg-[#12100E] p-8 text-white"
          >
            <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
              <div className="max-w-xl">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-[#D4AF37] px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-[#12100E]">
                  <Sparkles size={12} /> Le plus malin
                </span>
                <h3 className="font-heading mt-3 text-2xl font-extrabold sm:text-3xl">Pack Tout-Accès</h3>
                <p className="mt-2 text-sm text-white/70">
                  Hésiter entre deux rubriques, c'est fini : FodindinLago, FodindinLove, FodindinClubVIP
                  et FodindinAir-Bengue ouverts en même temps, photos nettes et numéros compris.
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {Object.values(CATEGORIES).map((c) => (
                    <span key={c.id} className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold">
                      <Check size={12} style={{ color: c.color }} /> {c.name}
                    </span>
                  ))}
                </div>
              </div>
              <div className="shrink-0 text-left md:text-right">
                <p className="text-xs uppercase tracking-wider text-white/50">
                  au lieu de {formatFCFA(PRIX_SEPARES[period])}
                </p>
                <div className="font-heading text-4xl font-extrabold text-[#D4AF37]" data-testid="pack-prix">
                  {formatFCFA(pricing.tout.pack[period])}
                </div>
                <p className="text-xs text-white/60">/ {LIBELLE_PERIODE[period]}</p>
                <p className="mt-1 text-xs font-bold text-[#8BD6A8]" data-testid="pack-economie">
                  Économie de {formatFCFA(PRIX_SEPARES[period] - pricing.tout.pack[period])}
                </p>
                <button
                  onClick={() => buy("pack", "tout", period)}
                  disabled={buying !== null || base?.kind === "pack"}
                  data-testid="buy-pack-tout"
                  className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#D4AF37] px-7 py-3 font-bold text-[#12100E] transition-transform hover:scale-[1.03] disabled:opacity-50 md:w-auto"
                >
                  {buying === "pack-tout" ? <Loader2 size={16} className="animate-spin" /> : null}
                  {base?.kind === "pack" ? "Pack actif" : "Prendre le Pack Tout-Accès"}
                </button>
              </div>
            </div>
          </motion.div>
        )}

        {/* suppléments d'accès croisé */}
        {base && pricing && base.kind !== "pack" && base.period !== "decouverte" && (
          <div className="mt-16" data-testid="addons-section">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-stone-900">Suppléments d'accès croisé</h2>
            <p className="text-stone-600 mt-2 text-sm">
              Ajoutés à votre abonnement <strong>{CATEGORIES[base.category].name}</strong>, pour la même période ({LIBELLE_PERIODE[base.period] || base.period}).
            </p>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl">
              {Object.values(CATEGORIES).filter((c) => c.id !== base.category).map((cat) => {
                const price = pricing[cat.id].addon[base.period];
                const has = access?.access[cat.id];
                const loyaltyFree = cat.id === "love" && access?.vip_monthly_loyalty;
                return (
                  <div key={cat.id} className="bg-white border border-[#E5DFD5] rounded-2xl p-5 flex items-center justify-between gap-4" data-testid={`addon-card-${cat.id}`}>
                    <div>
                      <div className="font-heading font-bold text-stone-900">Ajouter {cat.name}</div>
                      <div className="text-sm font-semibold mt-1" style={{ color: cat.color }}>
                        {loyaltyFree ? "Offert (fidélité VIP mensuel)" : `+${formatFCFA(price)} / ${LIBELLE_PERIODE[base.period] || base.period}`}
                      </div>
                    </div>
                    <button
                      onClick={() => buy("addon", cat.id, base.period)}
                      disabled={buying !== null || has}
                      data-testid={`buy-addon-${cat.id}`}
                      className={`shrink-0 inline-flex items-center gap-2 text-sm font-semibold px-5 py-2.5 rounded-full border-2 transition-all ${has ? "opacity-50 cursor-not-allowed border-stone-200 text-stone-400" : "hover:text-white"}`}
                      style={!has ? { borderColor: cat.color, color: cat.color } : {}}
                      onMouseEnter={(e) => { if (!has) e.currentTarget.style.backgroundColor = cat.color; }}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                    >
                      {has ? <Check size={15} /> : <Plus size={15} />} {has ? "Accès actif" : "Ajouter"}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </main>

      {/* Pop-up de guidage : prochaine étape = déposer une annonce */}
      {/* Paiement par un proche : lien à lui transmettre (SMS déjà envoyé). */}
      <AnimatePresence>
        {lienProche && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[85] flex items-end justify-center bg-black/60 p-0 backdrop-blur-sm sm:items-center sm:p-4"
            data-testid="lien-proche-modal">
            <div className="w-full rounded-t-[28px] bg-white p-6 sm:max-w-md sm:rounded-[28px] sm:p-8">
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-stone-400">
                Paiement par un proche
              </p>
              <h2 className="font-heading mt-2 text-2xl font-extrabold text-stone-900">
                {lienProche.nom ? `${lienProche.nom} peut payer` : "Votre proche peut payer"}
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-stone-600">
                {lienProche.sms
                  ? `Un SMS avec le lien de paiement vient de partir au +225 ${lienProche.numero || ""}.`
                  : "Transmettez-lui ce lien de paiement."}{" "}
                Votre abonnement s'activera sur <b>votre compte</b> dès que l'opérateur confirme.
              </p>
              <div className="mt-4 flex items-center gap-2 rounded-2xl border border-[#E5DFD5] bg-[#FAF8F5] px-3 py-2">
                <span className="min-w-0 flex-1 truncate text-xs font-semibold text-stone-600"
                  data-testid="lien-proche-url">{lienProche.lien}</span>
                <button onClick={() => { navigator.clipboard?.writeText(lienProche.lien); toast.success("Lien copié"); }}
                  data-testid="lien-proche-copier"
                  className="shrink-0 rounded-full bg-stone-900 px-3 py-1.5 text-xs font-bold text-white">
                  Copier
                </button>
              </div>
              <a href={`https://wa.me/?text=${encodeURIComponent(`Bonjour, peux-tu régler mon abonnement FodindinCa (${lienProche.montant} FCFA) ici : ${lienProche.lien}`)}`}
                target="_blank" rel="noreferrer" data-testid="lien-proche-whatsapp"
                className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#25D366] py-3 text-sm font-bold text-white">
                Envoyer par WhatsApp
              </a>
              <button onClick={() => { setLienProche(null); refresh(); }} data-testid="lien-proche-fermer"
                className="mt-2 w-full rounded-full border-2 border-stone-200 py-2.5 text-sm font-semibold text-stone-600">
                Fermer
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {offre && (
          <ChoixOperateurModal
            offre={offre}
            enCours={operateurEnCours}
            numeroInitial={user?.contact}
            onChoisir={payer}
            onClose={() => setOffre(null)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {guideAnnonce && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
            data-testid="guide-annonce-overlay"
          >
            <motion.div
              initial={{ opacity: 0, y: 32, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 32, scale: 0.95 }}
              transition={{ duration: 0.35 }}
              className="relative bg-white rounded-3xl w-full max-w-md p-8 text-center shadow-2xl"
              data-testid="guide-annonce-popup"
            >
              <button
                onClick={() => setGuideAnnonce(null)}
                data-testid="guide-annonce-close"
                className="absolute top-4 right-4 text-stone-400 hover:text-stone-700 transition-colors p-1"
              >
                <X size={18} />
              </button>
              <div
                className="mx-auto w-16 h-16 rounded-full flex items-center justify-center"
                style={{ backgroundColor: `${CATEGORIES[guideAnnonce].color}1A` }}
              >
                <Search size={30} style={{ color: CATEGORIES[guideAnnonce].color }} />
              </div>
              <h2 className="font-heading text-2xl font-extrabold text-stone-900 mt-5">
                Abonnement activé, {user.firstname} !
              </h2>
              <p className="text-stone-600 mt-3 leading-relaxed text-sm">
                Vous entrez dans <strong>{CATEGORIES[guideAnnonce].name}</strong>. Ouverture des annonces…
              </p>
              <button
                onClick={() => navigate(`/categorie/${guideAnnonce}/annonces`)}
                data-testid="guide-annonce-cta"
                className="mt-7 w-full inline-flex items-center justify-center gap-2 text-white font-semibold py-3.5 rounded-full transition-opacity hover:opacity-90"
                style={{ backgroundColor: CATEGORIES[guideAnnonce].color }}
              >
                <Search size={16} /> Voir les annonces maintenant
              </button>
              <button
                onClick={() => navigate(`/annonces?cat=${guideAnnonce}`)}
                data-testid="guide-annonce-deposer"
                className="mt-3 text-sm font-semibold text-stone-500 hover:text-stone-800 transition-colors inline-flex items-center gap-1.5"
              >
                <Megaphone size={14} /> Je veux plutôt déposer une annonce
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {transfert && (
        <PaiementTransfert offre={transfert} user={user}
          onFerme={() => setTransfert(null)}
          onDeclare={() => { setOffre(null); refresh(); }} />
      )}

      <Footer />
    </div>
  );
}
